import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  Edge,
  MarkerType,
  Node,
  NodeMouseHandler,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  WORKFLOW_EDGES,
  PARKED_GROUP,
  NODE,
} from '../../workflow/workflowConfig';
import { layoutWorkflow, DIAGRAM_HEIGHT, LaidOutNode } from './workflowLayout';
import WorkflowNode, { WorkflowNodeData } from './WorkflowNode';
import WorkflowGroupNode from './WorkflowGroupNode';

interface WorkflowDiagramProps {
  nodeCounts: Record<string, number>;
  priorityCounts: Record<string, number>;
  selectedStage: string | null;
  onSelectStage: (stage: string | null) => void;
}

const nodeTypes = { workflow: WorkflowNode, group: WorkflowGroupNode };

// The edge line color. A muted gray that reads on the page's light or dark background.
const EDGE_LIGHT = '#b0bec5';
const EDGE_DARK = '#546e7a';

// Declare each node's size to React Flow rather than letting it discover the size by measuring the
// DOM. This is what keeps the diagram on screen: the nodes array below is rebuilt whenever the PR
// counts change (e.g. a background GitHub refresh writes new pullRequests docs), and React Flow
// discards everything it measured for any node object it hasn't seen before. An unmeasured node is
// rendered `visibility: hidden` and an edge whose endpoints have no handle bounds isn't rendered at
// all, so the whole diagram went blank until the page was reloaded.
//   - `width`/`height` are React Flow's supported way to state a size it doesn't need to measure,
//     and are what it uses for the node's inline size.
//   - `measured` says "this node is already sized", which makes React Flow carry the handle bounds
//     it measured earlier over to the rebuilt node instead of dropping them, so the edges survive.
// workflowLayout is pure geometry with fixed node sizes, so these are exactly what a measurement
// would report.
function declaredSize(ln: LaidOutNode): Pick<Node, 'width' | 'height' | 'measured'> {
  return { width: ln.width, height: ln.height, measured: { width: ln.width, height: ln.height } };
}

// Read-only lane diagram of the PR lifecycle. Positions come from workflowLayout, computed for the
// measured container width so the diagram fills the full width at 100% zoom (it only zooms out below
// MIN_WIDTH). The only interaction is clicking a node to filter the table below.
const WorkflowDiagram: React.FC<WorkflowDiagramProps> = ({
  nodeCounts,
  priorityCounts,
  selectedStage,
  onSelectStage,
}) => {
  const theme = useTheme();
  const edgeColor = theme.palette.mode === 'dark' ? EDGE_DARK : EDGE_LIGHT;

  // Measure the container so the layout can flex the column gaps to fill the width.
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const layout = useMemo(() => layoutWorkflow(width || 1200), [width]);

  const nodes: Node[] = useMemo(
    () =>
      layout.nodes.map(ln => {
        const def = ln.def;
        if (def.variant === 'group') {
          return {
            id: ln.id,
            type: 'group',
            position: { x: ln.x, y: ln.y },
            data: { label: def.diagramLabel, outletOffsetY: ln.outletOffsetY },
            ...declaredSize(ln),
            // React Flow's built-in .react-flow__node-group styling adds a dark border, its own fill
            // and 10px of padding. The padding insets our shaded panel while the child nodes are
            // still positioned from the node's outer edge, so they spill past the shading — clear
            // all three and let WorkflowGroupNode paint the full node rect itself.
            style: { padding: 0, border: 'none', background: 'transparent' },
            draggable: false,
            selectable: false,
            connectable: false,
          } as Node;
        }
        const data: WorkflowNodeData = {
          variant: def.variant,
          label: def.diagramLabel,
          fullLabel: def.label,
          count: nodeCounts[def.id] ?? 0,
          priorityCount: priorityCounts[def.id] ?? 0,
          isSelected: selectedStage === def.id,
          kind: def.kind,
          failure: def.failure ?? false,
          inlets: ln.inlets,
          outletOffsetY: ln.outletOffsetY,
        };
        return {
          id: ln.id,
          type: 'workflow',
          position: { x: ln.x, y: ln.y },
          data,
          ...declaredSize(ln),
          ...(ln.parentId ? { parentId: ln.parentId, extent: 'parent' as const } : {}),
          draggable: false,
          connectable: false,
          selectable: false,
        } as Node;
      }),
    [layout, nodeCounts, priorityCounts, selectedStage]
  );

  const edges: Edge[] = useMemo(
    () =>
      WORKFLOW_EDGES.map((e, i) => {
        // The gate (Ready) has a dedicated inlet handle per incoming lane and one outlet to Merged,
        // so every lane line stays straight; other nodes attach by side (s-<side> / t-<side>).
        const sourceHandle = e.source === NODE.ready ? 'out-merged' : 's-' + (e.sourceSide ?? 'right');
        const targetHandle = e.target === NODE.ready ? 'in-' + e.source : 't-' + (e.targetSide ?? 'left');
        return {
          id: `e${i}-${e.source}-${e.target}`,
          source: e.source,
          target: e.target,
          sourceHandle,
          targetHandle,
          type: 'default',
          markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12, color: edgeColor },
          style: { stroke: edgeColor, strokeWidth: 1.5 },
        };
      }),
    [edgeColor]
  );

  // Toggle: clicking the selected node clears the filter. The Parked container isn't a stage, so a
  // click on it deselects like the backdrop does.
  const handleNodeClick: NodeMouseHandler = (_event, node: Node) => {
    if (node.id === PARKED_GROUP) {
      onSelectStage(null);
      return;
    }
    onSelectStage(selectedStage === node.id ? null : node.id);
  };

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: DIAGRAM_HEIGHT }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={handleNodeClick}
        onPaneClick={() => onSelectStage(null)}
        viewport={{ x: 0, y: 0, zoom: layout.zoom }}
        onViewportChange={() => {}}
        minZoom={0.2}
        maxZoom={1}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        nodesFocusable={false}
        edgesFocusable={false}
        panOnDrag={false}
        panOnScroll={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
      />
    </Box>
  );
};

export default WorkflowDiagram;
